// 261010 Epic 2 / P4b-2 — bộ điều phối poll THUẦN của một thẻ location (Quyết định 7). Không React / antd ⇒ test bằng jest fake timers; hook React chỉ bọc mỏng bộ này.
// Vì sao tab tự poll: job `FILM_LOCATION_VIEW` không mang `planId` nên `workflow-status` của plan KHÔNG thấy nó ⇒ trang không poll giúp (chỉ poll khi `status.jobs` có phần tử).
// Luật: ≥1 view GENERATING ⇒ gọi lại `fetchViews` mỗi 8 giây · request trước chưa về ⇒ bỏ nhịp · hết GENERATING ⇒ dừng · lỗi mạng ⇒ giữ dữ liệu cũ, báo lỗi, DỪNG (Tải lại mới bật lại).
import type { LocationView, LocationViewsResponse, LocationViewStatus } from '../../types/filmLocationAssets';

export const POLL_MS = 8000;

export const shouldPoll = (views: LocationView[] | null | undefined): boolean => !!views && views.some((x) => x.status === 'GENERATING');

export interface ViewTransition { id: string; from: 'GENERATING'; to: LocationViewStatus }
/** View có ở cả hai lượt, trước GENERATING và nay không còn ⇒ "vừa xong" (FRESH / ERROR / STALE). */
export function finishedTransitions(prev: LocationView[] | null | undefined, next: LocationView[] | null | undefined): ViewTransition[] {
  if (!prev || !next) return [];
  const nowById = new Map(next.map((x) => [x.id, x]));
  const out: ViewTransition[] = [];
  for (const p of prev) {
    if (p.status !== 'GENERATING') continue;
    const n = nowById.get(p.id);
    if (n && n.status !== 'GENERATING') out.push({ id: p.id, from: 'GENERATING', to: n.status });
  }
  return out;
}

export interface LocationViewPollerDeps {
  fetchViews: () => Promise<LocationViewsResponse>;
  /** Dữ liệu mới của một lượt poll / Tải lại (không gọi khi lỗi). */
  onData: (r: LocationViewsResponse) => void;
  /** Một view vừa GENERATING → xong: kèm dữ liệu của CHÍNH lượt đó. */
  onFinished?: (t: ViewTransition[], r: LocationViewsResponse) => void;
  /** Lượt poll / Tải lại lỗi mạng: poll đã dừng, dữ liệu cũ giữ nguyên. */
  onError?: (e: unknown) => void;
  intervalMs?: number;
}

export interface LocationViewPoller {
  /** Host báo "đây là dữ liệu mới nhất" (nạp đầu, sau thao tác). Bật / tắt timer theo `shouldPoll`; không tự bật lại khi đang lỗi. */
  sync: (views: LocationView[]) => void;
  /** Nút "Tải lại": gỡ cờ lỗi, nạp ngay một lượt, bật lại poll nếu còn GENERATING. */
  reload: () => Promise<void>;
  stop: () => void;
  isActive: () => boolean;
  isErrored: () => boolean;
}

export function createLocationViewPoller(deps: LocationViewPollerDeps): LocationViewPoller {
  const intervalMs = deps.intervalMs ?? POLL_MS;
  let current: LocationView[] | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let inflight = false;
  let errored = false;
  let stopped = false;
  let gen = 0; // tăng mỗi lần `sync`: kết quả poll xuất phát TRƯỚC một `sync` mới hơn là dữ liệu cũ ⇒ bỏ

  const clear = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
  const arm = () => {
    if (stopped) return;
    if (!errored && shouldPoll(current)) { if (timer === null) timer = setInterval(() => { void run(false); }, intervalMs); } else clear();
  };

  async function run(manual: boolean): Promise<void> {
    if (stopped || inflight) return; // không chồng: bỏ nhịp
    inflight = true;
    const myGen = gen;
    try {
      const r = await deps.fetchViews();
      if (stopped) return;
      if (myGen !== gen) return; // đã có dữ liệu mới hơn (sync) ⇒ bỏ kết quả cũ; timer do `sync` lo
      const prev = current;
      current = r.views;
      deps.onData(r);
      const done = finishedTransitions(prev, r.views);
      if (done.length) deps.onFinished?.(done, r);
    } catch (e) {
      if (stopped) return;
      errored = true; clear();
      deps.onError?.(e);
      return;
    } finally {
      inflight = false;
    }
    if (manual) errored = false;
    arm();
  }

  return {
    sync(views) {
      if (stopped) return;
      gen += 1;
      current = views;
      arm();
    },
    async reload() {
      if (stopped) return;
      errored = false;
      await run(true);
    },
    stop() { stopped = true; clear(); },
    isActive: () => timer !== null,
    isErrored: () => errored,
  };
}
